import React, { useState } from 'react';
import {
  Shield,
  ShieldAlert,
  ShieldCheck,
  AlertTriangle,
  Power,
  PowerOff,
  Clock,
  Activity,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import {
  useRiskStatus,
  useRiskShutdownStatus,
  useRiskActivateShutdown,
  useRiskDeactivateShutdown,
  useRiskEvents,
} from '@/hooks/useApi';
import { Card, Badge, LoadingSpinner } from '@/components/UI';

/* ---------- Inline Button (until a shared one is added to UI.tsx) ---------- */
interface BtnProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'danger' | 'secondary' | 'ghost';
  size?: 'sm' | 'md';
  icon?: React.ReactNode;
}

function Button({ variant = 'primary', size = 'md', icon, children, className, ...rest }: BtnProps) {
  const base = 'inline-flex items-center justify-center gap-2 font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed';
  const sizes = size === 'sm' ? 'px-2.5 py-1 text-xs' : 'px-4 py-2 text-sm';
  const variants: Record<string, string> = {
    primary: 'bg-brand-600 text-white hover:bg-brand-700',
    danger: 'bg-loss/20 text-loss hover:bg-loss/30',
    secondary: 'bg-surface-700 text-surface-200 hover:bg-surface-600',
    ghost: 'text-surface-400 hover:text-surface-200 hover:bg-surface-800',
  };
  return (
    <button className={`${base} ${sizes} ${variants[variant]} ${className ?? ''}`} {...rest}>
      {icon}{children}
    </button>
  );
}

/* ---------- Shutdown Panel ---------- */
function ShutdownPanel() {
  const { data: shutdownStatus, isLoading: loadingStatus } = useRiskShutdownStatus();
  const activateMutation = useRiskActivateShutdown();
  const deactivateMutation = useRiskDeactivateShutdown();
  const [showActivateDialog, setShowActivateDialog] = useState(false);
  const [showDeactivateDialog, setShowDeactivateDialog] = useState(false);
  const [reason, setReason] = useState('');
  const [confirmDeactivate, setConfirmDeactivate] = useState(false);

  if (loadingStatus) return <LoadingSpinner text="Loading shutdown status..." />;

  const isActive = shutdownStatus?.is_active ?? false;

  const handleActivate = () => {
    if (!reason.trim()) return;
    activateMutation.mutate(reason.trim(), {
      onSuccess: () => {
        setShowActivateDialog(false);
        setReason('');
      },
    });
  };

  const handleDeactivate = () => {
    if (!confirmDeactivate) return;
    deactivateMutation.mutate(true, {
      onSuccess: () => {
        setShowDeactivateDialog(false);
        setConfirmDeactivate(false);
      },
    });
  };

  return (
    <Card title="Emergency Shutdown">
      <div className="space-y-4">
        {/* Status indicator */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {isActive ? (
              <div className="w-12 h-12 rounded-full bg-loss/20 flex items-center justify-center">
                <ShieldAlert className="w-6 h-6 text-loss" />
              </div>
            ) : (
              <div className="w-12 h-12 rounded-full bg-profit/20 flex items-center justify-center">
                <ShieldCheck className="w-6 h-6 text-profit" />
              </div>
            )}
            <div>
              <p className="text-lg font-bold text-surface-100">
                {isActive ? 'SHUTDOWN ACTIVE' : 'Trading Active'}
              </p>
              {isActive && shutdownStatus?.reason && (
                <p className="text-sm text-loss">{shutdownStatus.reason}</p>
              )}
              {isActive && shutdownStatus?.activated_at && (
                <p className="text-xs text-surface-500 mt-0.5">
                  Activated: {new Date(shutdownStatus.activated_at).toLocaleString()}
                </p>
              )}
            </div>
          </div>
          <Badge variant={isActive ? 'loss' : 'profit'}>
            {isActive ? 'BLOCKED' : 'OPERATIONAL'}
          </Badge>
        </div>

        {/* Action buttons */}
        <div className="flex gap-3">
          {!isActive ? (
            <Button
              variant="danger"
              onClick={() => setShowActivateDialog(true)}
              icon={<Power className="w-4 h-4" />}
            >
              Activate Shutdown
            </Button>
          ) : (
            <Button
              variant="secondary"
              onClick={() => setShowDeactivateDialog(true)}
              icon={<PowerOff className="w-4 h-4" />}
            >
              Deactivate Shutdown
            </Button>
          )}
        </div>

        {/* Activate dialog */}
        {showActivateDialog && (
          <div className="p-4 rounded-lg bg-surface-800/80 border border-loss/30 space-y-3">
            <div className="flex items-center gap-2 text-loss">
              <AlertTriangle className="w-5 h-5" />
              <p className="font-semibold">Activate Emergency Shutdown</p>
            </div>
            <p className="text-sm text-surface-400">
              This will immediately halt ALL trading across all accounts. Use only in emergency situations.
            </p>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Enter reason for shutdown..."
              className="w-full px-3 py-2 bg-surface-900 border border-surface-700 rounded-lg text-surface-200 text-sm focus:outline-none focus:border-brand-500"
            />
            <div className="flex gap-2">
              <Button
                variant="danger"
                onClick={handleActivate}
                disabled={!reason.trim() || activateMutation.isPending}
              >
                {activateMutation.isPending ? 'Activating...' : 'Confirm Activation'}
              </Button>
              <Button variant="ghost" onClick={() => { setShowActivateDialog(false); setReason(''); }}>
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* Deactivate dialog */}
        {showDeactivateDialog && (
          <div className="p-4 rounded-lg bg-surface-800/80 border border-brand-500/30 space-y-3">
            <div className="flex items-center gap-2 text-brand-400">
              <ShieldCheck className="w-5 h-5" />
              <p className="font-semibold">Resume Trading</p>
            </div>
            <p className="text-sm text-surface-400">
              This will deactivate the emergency shutdown and resume trading. Please confirm you want to proceed.
            </p>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={confirmDeactivate}
                onChange={(e) => setConfirmDeactivate(e.target.checked)}
                className="w-4 h-4 rounded border-surface-600 bg-surface-800 text-brand-600 focus:ring-brand-500"
              />
              <span className="text-sm text-surface-300">
                I confirm I want to resume trading
              </span>
            </label>
            <div className="flex gap-2">
              <Button
                variant="primary"
                onClick={handleDeactivate}
                disabled={!confirmDeactivate || deactivateMutation.isPending}
              >
                {deactivateMutation.isPending ? 'Deactivating...' : 'Confirm Deactivation'}
              </Button>
              <Button variant="ghost" onClick={() => { setShowDeactivateDialog(false); setConfirmDeactivate(false); }}>
                Cancel
              </Button>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}

/* ---------- Risk Status Panel ---------- */
function RiskStatusPanel() {
  const { data: riskStatus, isLoading } = useRiskStatus();

  if (isLoading) return <LoadingSpinner text="Loading risk status..." />;

  const shutdown = riskStatus?.shutdown;
  const cooldowns = riskStatus?.active_cooldowns ?? {};
  const events24h = riskStatus?.total_risk_events_24h ?? 0;
  const events7d = riskStatus?.total_risk_events_7d ?? 0;

  return (
    <Card title="Risk Engine Status">
      <div className="space-y-4">
        {/* Overview stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="p-3 rounded-lg bg-surface-800/50">
            <p className="text-xs text-surface-500">Shutdown</p>
            <p className={`text-lg font-bold ${shutdown?.is_active ? 'text-loss' : 'text-profit'}`}>
              {shutdown?.is_active ? 'ACTIVE' : 'Inactive'}
            </p>
          </div>
          <div className="p-3 rounded-lg bg-surface-800/50">
            <p className="text-xs text-surface-500">Active Cooldowns</p>
            <p className="text-lg font-bold text-surface-100">
              {Object.keys(cooldowns).length}
            </p>
          </div>
          <div className="p-3 rounded-lg bg-surface-800/50">
            <p className="text-xs text-surface-500">Events (24h)</p>
            <p className={`text-lg font-bold ${events24h > 5 ? 'text-warning' : 'text-surface-100'}`}>
              {events24h}
            </p>
          </div>
          <div className="p-3 rounded-lg bg-surface-800/50">
            <p className="text-xs text-surface-500">Events (7d)</p>
            <p className={`text-lg font-bold ${events7d > 20 ? 'text-warning' : 'text-surface-100'}`}>
              {events7d}
            </p>
          </div>
        </div>

        {/* Active cooldowns list */}
        {Object.keys(cooldowns).length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-surface-400 mb-2">Active Cooldowns</h4>
            <div className="space-y-1">
              {Object.entries(cooldowns).map(([accountId, remaining]) => (
                <div key={accountId} className="flex items-center justify-between p-2 rounded bg-surface-800/30">
                  <div className="flex items-center gap-2">
                    <Clock className="w-3.5 h-3.5 text-warning" />
                    <span className="text-xs text-surface-300 font-mono">{accountId.slice(0, 8)}...</span>
                  </div>
                  <span className="text-xs text-warning">{String(remaining)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}

/* ---------- Risk Events Panel ---------- */
function RiskEventsPanel() {
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState(true);
  const { data: eventsData, isLoading } = useRiskEvents({ page, page_size: 10 });

  if (isLoading) return <LoadingSpinner text="Loading risk events..." />;

  const events = eventsData?.events ?? [];
  const total = eventsData?.total ?? 0;

  const severityVariant = (severity: string): 'loss' | 'warning' | 'brand' | 'neutral' => {
    switch (severity?.toLowerCase()) {
      case 'critical': return 'loss';
      case 'warning': return 'warning';
      case 'info': return 'brand';
      default: return 'neutral';
    }
  };

  return (
    <Card
      title="Risk Events"
      action={
        <div className="flex items-center gap-2">
          <Badge variant="neutral">{total} total</Badge>
          <button onClick={() => setExpanded(!expanded)} className="text-surface-500 hover:text-surface-300">
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </div>
      }
    >
      {expanded && (
        <div className="space-y-2">
          {events.length === 0 ? (
            <p className="text-sm text-surface-500 text-center py-4">No risk events recorded</p>
          ) : (
            events.map((event: any) => (
              <div key={event.id} className="p-3 rounded-lg bg-surface-800/40 border border-surface-800">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge variant={severityVariant(event.severity)}>
                        {event.severity?.toUpperCase() ?? 'INFO'}
                      </Badge>
                      <span className="text-xs text-surface-500 font-mono">
                        {event.event_type}
                      </span>
                    </div>
                    <p className="text-sm text-surface-300">{event.message}</p>
                    {event.account_id && (
                      <p className="text-xs text-surface-600 mt-1">
                        Account: {event.account_id.slice(0, 8)}...
                      </p>
                    )}
                  </div>
                  <span className="text-xs text-surface-600 whitespace-nowrap">
                    {event.created_at ? new Date(event.created_at).toLocaleString() : ''}
                  </span>
                </div>
              </div>
            ))
          )}

          {/* Pagination */}
          {total > 10 && (
            <div className="flex items-center justify-between pt-2">
              <Button
                variant="ghost"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage(page - 1)}
              >
                Previous
              </Button>
              <span className="text-xs text-surface-500">
                Page {page} of {Math.ceil(total / 10)}
              </span>
              <Button
                variant="ghost"
                size="sm"
                disabled={page >= Math.ceil(total / 10)}
                onClick={() => setPage(page + 1)}
              >
                Next
              </Button>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

/* ---------- Main Risk Page ---------- */
export function RiskPage() {
  return (
    <div className="space-y-6 animate-in">
      <div className="flex items-center gap-3">
        <Shield className="w-7 h-7 text-brand-500" />
        <h1 className="text-2xl font-bold text-surface-100">Risk Management</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ShutdownPanel />
        <RiskStatusPanel />
      </div>

      <RiskEventsPanel />
    </div>
  );
}
